import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { PostHogProvider } from "posthog-js/react";
import { StrictMode } from "react";
import { createRoot, type ErrorInfo } from "react-dom/client";
import { ApiError } from "./api.ts";
import { ToastProvider } from "./components/ui/toast.tsx";
import { captureError, initAnalytics, posthog, track } from "./lib/analytics.ts";
import { listenForInstallPrompt } from "./lib/install-prompt.ts";
import { applyStoredNight, NightProvider } from "./lib/night.tsx";
import { DeviceError } from "./lib/push.ts";
import { router } from "./router.tsx";

// Before the first paint: a 3am open in night mode must not flash white.
applyStoredNight();
// Facebook appends "#_=_" to its sign-in redirect, and the browser keeps it
// through the redirects after it; nothing here uses it.
if (window.location.hash === "#_=_")
  history.replaceState(history.state, "", window.location.pathname + window.location.search);
initAnalytics();
// Android's install prompt can fire before the log renders.
listenForInstallPrompt();

/** How a request failed: refused by the server (with its status) or never answered. */
function failure(error: unknown) {
  return error instanceof ApiError && error.status !== undefined
    ? { status: error.status }
    : { status: null, network: true };
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 10_000, retry: 1, refetchOnWindowFocus: false },
  },
  // Every failed write is one a parent saw roll back; reads fail quietly on a
  // flaky connection, so only server errors are worth recording.
  mutationCache: new MutationCache({
    onError: (error, _vars, _ctx, mutation) => {
      // A declined notification prompt never reached the server.
      if (error instanceof DeviceError) return;
      track("api write failed", {
        action: mutation.meta?.action ?? "other",
        ...failure(error),
      });
    },
  }),
  queryCache: new QueryCache({
    onError: (error, query) => {
      const { status } = failure(error);
      if (status !== null && status >= 500)
        track("api read failed", { query: String(query.queryKey[0]), status });
    },
  }),
});

/** React's own error reports: a crashed render, or one a route's error boundary caught. */
function reportReactError(handled: boolean) {
  return (error: unknown, info: ErrorInfo) => {
    console.error(error);
    captureError(error, { handled, component_stack: info.componentStack ?? undefined });
  };
}

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("#root not found");

createRoot(rootEl, {
  onUncaughtError: reportReactError(false),
  onCaughtError: reportReactError(true),
}).render(
  <StrictMode>
    <PostHogProvider client={posthog}>
      <QueryClientProvider client={queryClient}>
        <NightProvider>
          <ToastProvider>
            <RouterProvider router={router} />
          </ToastProvider>
        </NightProvider>
      </QueryClientProvider>
    </PostHogProvider>
  </StrictMode>,
);
